import { useState, useCallback } from "react";
import { UserSearch } from "lucide-react";
import {
  useAdminSearchUsers,
  useAdminUserDetail,
} from "../hooks/useUsers";
import { useLocale } from "../../../context/LanguageContext";
import { UserSearchResults } from "./UserSearchResults";
import { UserDetailPanel } from "./UserDetailPanel";
import type { AdminSearchUserRow } from "../types";

const PAGE_SIZE = 20;

export function UserSearchPage() {
  const { t } = useLocale();

  // ─── State ───────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // ─── Queries ─────────────────────────────────────────────────────────
  const { data, loading } = useAdminSearchUsers(
    debouncedSearch,
    PAGE_SIZE,
    page * PAGE_SIZE,
  );
  const users = data?.adminSearchUsers?.users ?? [];
  const totalCount = data?.adminSearchUsers?.totalCount ?? 0;

  const { data: detailData, loading: detailLoading } = useAdminUserDetail(
    selectedUserId ?? "",
  );
  const userDetail = detailData?.adminUserDetail;

  // ─── Handlers ────────────────────────────────────────────────────────
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    setSelectedUserId(null);
    const id = setTimeout(() => setDebouncedSearch(value), 300);
    return () => clearTimeout(id);
  }, []);

  const handleSelectUser = useCallback((user: AdminSearchUserRow) => {
    setSelectedUserId(user._id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedUserId(null);
  }, []);

  return (
    <div className="flex h-full flex-col gap-6">
      {/* Header */}
      <div className="flex-shrink-0">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <UserSearch className="h-7 w-7" />
          {t("users.lookup.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("users.lookup.subtitle")}
        </p>
      </div>

      {/* Prominent Search Bar */}
      <div className="relative flex-shrink-0">
        <UserSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={t("adminUI.users.searchPlaceholder")}
          autoFocus
          className="h-12 w-full rounded-lg border border-border bg-card pl-12 pr-4 text-base text-foreground shadow-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        {debouncedSearch.length >= 2 && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {t("users.lookup.resultCount", { count: totalCount })}
          </span>
        )}
      </div>

      {/* Hint */}
      {debouncedSearch.length < 2 && debouncedSearch.length > 0 && (
        <p className="flex-shrink-0 text-sm text-muted-foreground">
          {t("users.lookup.minCharsHint")}
        </p>
      )}

      {/* Content area: both sides independently scrollable */}
      {debouncedSearch.length >= 2 && (
        <div className="flex min-h-0 flex-1 gap-6">
          {/* Search Results */}
          <div
            className={`overflow-y-auto transition-all duration-200 ${
              selectedUserId ? "w-2/5" : "w-full"
            }`}
          >
            <UserSearchResults
              users={users}
              totalCount={totalCount}
              isLoading={loading}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              selectedUserId={selectedUserId}
              onSelectUser={handleSelectUser}
            />
          </div>

          {/* Detail Panel (independently scrollable, pinned to viewport height) */}
          {selectedUserId && (
            <div className="w-3/5 overflow-y-auto animate-in slide-in-from-right">
              <UserDetailPanel
                userDetail={userDetail ?? null}
                isLoading={detailLoading}
                onClose={handleCloseDetail}
              />
            </div>
          )}
        </div>
      )}

      {/* Empty state when no search yet */}
      {!debouncedSearch && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="text-5xl">🔍</span>
          <p className="mt-4 text-lg font-medium text-foreground">
            {t("users.lookup.emptyTitle")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("users.lookup.emptyHint")}
          </p>
        </div>
      )}
    </div>
  );
}
